import React from 'react'
import classNames from 'classnames'

import type { PlatformReviewItem } from './type'

const clampRating = (rating: number, maxRating: number) => {
  if (Number.isNaN(rating)) return 0
  if (rating < 0) return 0
  if (rating > maxRating) return maxRating
  return rating
}

const STAR_PATH =
  'M12 2.5l2.9 6.1 6.6.9-4.8 4.7 1.2 6.6L12 17.8 6.1 20.8l1.2-6.6-4.8-4.7 6.6-.9z'

const Star: React.FC<{ fillPercentage: number; color: string }> = ({
  fillPercentage,
  color,
}) => {
  const clamped = Math.max(0, Math.min(1, fillPercentage))

  return (
    <div
      className="position-relative overflow-hidden"
      style={{ width: 20, height: 20 }}
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        aria-hidden="true"
        className="stroke-none fill-shade-40"
      >
        <path d={STAR_PATH} />
      </svg>
      <div
        className="position-absolute"
        style={{ inset: 0, width: `${clamped * 100}%`, overflow: 'hidden' }}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          aria-hidden="true"
          className={`stroke-none ${color}`}
        >
          <path d={STAR_PATH} />
        </svg>
      </div>
    </div>
  )
}

export const PlatformReview: React.FC<
  PlatformReviewItem & {
    className?: string
    color?: string
    variant?: 'center' | 'default'
  }
> = ({
  rating,
  maxRating = 5,
  platform,
  link,
  className,
  color = 'primary',
  variant = 'default',
}) => {
  const safeMax = maxRating > 0 ? maxRating : 5
  const safeRating = clampRating(rating, safeMax)
  const ratingLabel = `${safeRating.toFixed(1)}/${safeMax}`
  const onLabel = 'on'

  const rootClassName = classNames(
    'flexbox flexbox-column flexbox-gap-6 shade-05',
    'font-size-large',
    variant === 'center' ? 'flexbox-align-center' : 'text-align-left',
    link && 'pointer',
    className
  )
  const content = (
    <>
      <div className="flexbox flexbox-align-center flexbox-gap-2">
        {Array.from({ length: safeMax }).map((_, index) => {
          const starIndex = index + 1
          const fill = Math.max(0, Math.min(1, safeRating - (starIndex - 1)))
          return (
            <Star
              key={starIndex}
              fillPercentage={fill}
              color={`fill-${color}`}
            />
          )
        })}
      </div>
      <div className="flexbox flexbox-align-center flexbox-gap-4 font-weight-semibold">
        <span className="font-weight-bold">{ratingLabel}</span>
        <span>{onLabel}</span>
        <span className={link ? 'underline' : undefined}>{platform}</span>
      </div>
    </>
  )

  if (!link) {
    return (
      <div aria-label={platform} className={rootClassName}>
        {content}
      </div>
    )
  }

  return (
    <a
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={platform}
      className={rootClassName}
    >
      {content}
    </a>
  )
}
